'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { isFilled } from '@prismicio/helpers';
import { RichText } from '@/app/lib/RichText';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { BackgroundImage } from "@/app/lib/ParallaxBackground";
import styles from './dynamicForm.module.scss';
import sharedStyles from '@/app/shared.module.scss';
import { sliceStyle, dashify } from '@/app/lib/utils';
import { loadTurnstileScript, turnstileUserMessage, waitForTurnstileToken } from '@/app/lib/DynamicForm/turnstile';

const FILE_UPLOAD_FIELD_VARIATION = 'fileUpload';
const UPLOADCARE_SCRIPT_URL = 'https://ucarecdn.com/libs/widget/3.x/uploadcare.full.min.js';

function formColorStyles(formData, slicePrimary) {
	return {
		'--form-bg-color': slicePrimary?.form_background_color ?? undefined,
		'--field-bg-color': formData?.field_background_color ?? undefined,
		'--field-text-color': formData?.field_text_color ?? undefined,
		'--field-error-color': formData?.field_error_color ?? undefined,
		'--button-text-color': formData?.button_text_color ?? undefined,
		'--button-text-hover-color': formData?.button_text_hover_color ?? undefined,
		'--button-bg-color': formData?.button_background_color ?? undefined,
		'--button-bg-hover-color': formData?.button_background_hover_color ?? undefined,
		'--upload-btn-text-color': formData?.upload_button_text_color ?? undefined,
		'--upload-btn-text-hover-color': formData?.upload_button_text_hover_color ?? undefined,
		'--upload-btn-bg-color': formData?.upload_button_background_color ?? undefined,
		'--upload-btn-bg-hover-color': formData?.upload_button_background_hover_color ?? undefined,
	};
}

export default function DynamicForm({ form, slicePrimary }) {
	const [submissionState, setSubmissionState] = useState('initial');
	const [submissionError, setSubmissionError] = useState(false);
	const [turnstileError, setTurnstileError] = useState('');
	const [turnstileInteractive, setTurnstileInteractive] = useState(false);

	const scriptElementRef = useRef(null);
	const turnstileContainerRef = useRef(null);
	const turnstileWidgetIdRef = useRef(null);
	const turnstileTokenRef = useRef(null);
	const turnstileMountIdRef = useRef(0);
	const formSliceRef = useRef(null);

	// A list of file upload field UIDs to check in onSubmit
	const fileUploadFieldUids = useRef([]);

	const cleanupTurnstileWidget = useCallback(() => {
		if (turnstileWidgetIdRef.current !== null && window.turnstile) {
			window.turnstile.remove(turnstileWidgetIdRef.current);
		}
		turnstileWidgetIdRef.current = null;
		turnstileTokenRef.current = null;
	}, []);

	const mountTurnstileWidget = useCallback(async () => {
		const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
		const mountId = ++turnstileMountIdRef.current;

		if (!siteKey) {
			setTurnstileError('Security check is not configured. Please contact support.');
			console.error('NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set');
			return;
		}

		setTurnstileError('');
		turnstileTokenRef.current = null;
		cleanupTurnstileWidget();

		for (let attempt = 0; attempt < 40; attempt += 1) {
			if (mountId !== turnstileMountIdRef.current) return;
			if (turnstileContainerRef.current) break;
			await new Promise((resolve) => setTimeout(resolve, 50));
		}

		if (!turnstileContainerRef.current) {
			setTurnstileError(turnstileUserMessage());
			return;
		}

		try {
			const turnstile = await loadTurnstileScript();
			if (mountId !== turnstileMountIdRef.current || !turnstileContainerRef.current) return;

			turnstileWidgetIdRef.current = turnstile.render(turnstileContainerRef.current, {
				sitekey: siteKey,
				appearance: 'interaction-only',
				retry: 'auto',
				'refresh-expired': 'auto',
				callback: (token) => {
					turnstileTokenRef.current = token;
					setTurnstileInteractive(false);
					setTurnstileError('');
				},
				'before-interactive-callback': () => {
					setTurnstileInteractive(true);
				},
				'after-interactive-callback': () => {
					setTurnstileInteractive(false);
				},
				'expired-callback': () => {
					turnstileTokenRef.current = null;
					setTurnstileInteractive(false);
					setTurnstileError(turnstileUserMessage());
				},
				'error-callback': (errorCode) => {
					turnstileTokenRef.current = null;
					setTurnstileInteractive(false);
					setTurnstileError(turnstileUserMessage(errorCode));
					console.error('Turnstile widget error:', errorCode);
				},
			});
		} catch (error) {
			if (mountId !== turnstileMountIdRef.current) return;
			turnstileTokenRef.current = null;
			setTurnstileError(turnstileUserMessage());
			console.error('Turnstile load error:', error);
		}
	}, [cleanupTurnstileWidget]);

	const resetTurnstileWidget = useCallback(() => {
		if (turnstileWidgetIdRef.current !== null && window.turnstile) {
			turnstileTokenRef.current = null;
			window.turnstile.reset(turnstileWidgetIdRef.current);
			return;
		}

		void mountTurnstileWidget();
	}, [mountTurnstileWidget]);

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const script = document.createElement('script');
		script.src = UPLOADCARE_SCRIPT_URL;
		script.async = true;
		script.charset = 'utf-8';

		window.UPLOADCARE_PUBLIC_KEY = process.env.NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY;

		document.head.appendChild(script);
		scriptElementRef.current = script;

		return () => {
			if (document.head.contains(scriptElementRef.current)) {
				document.head.removeChild(scriptElementRef.current);
			}
			delete window.UPLOADCARE_PUBLIC_KEY;
		};
	}, []);

	useEffect(() => {
		if (submissionState === 'success') return undefined;

		void mountTurnstileWidget();

		return () => {
			turnstileMountIdRef.current += 1;
			cleanupTurnstileWidget();
		};
	}, [submissionState, cleanupTurnstileWidget, mountTurnstileWidget]);

	useEffect(() => {
		if (submissionState !== 'success') return;
		if (!formSliceRef.current) return;

		const rect = formSliceRef.current.getBoundingClientRect();
		const absoluteTop = window.scrollY + rect.top;
		const viewportOffset = window.innerHeight * 0.3;

		window.scrollTo({
			top: Math.max(0, absoluteTop - viewportOffset),
			behavior: 'smooth',
		});
	}, [submissionState]);

	const { validationSchema, defaultValues } = React.useMemo(() => {
		const schemaFields = {};
		const formDefaultValues = {};
		const uids = [];

		form?.data?.slices?.forEach((fieldSlice) => {
			const fieldUid = dashify(fieldSlice.primary.uid, { separator: '_' }) || dashify(fieldSlice.primary.title, { separator: '_' });
			let fieldSchema = Yup.string();

			if (fieldSlice.variation === FILE_UPLOAD_FIELD_VARIATION) {
				uids.push(fieldUid);
			}

			if (fieldSlice.variation === 'checkboxes') {
				formDefaultValues[fieldUid] = [];
				fieldSchema = Yup.array();
			} else if (fieldSlice.variation === 'radio' || fieldSlice.variation === 'dropdown') {
				formDefaultValues[fieldUid] = '';
			} else {
				formDefaultValues[fieldUid] = '';
			}

			if (fieldSlice.primary.required) {
				fieldSchema = fieldSchema.required(`This field is required`);
			}

			if (fieldUid.toLowerCase() === 'email') {
				fieldSchema = fieldSchema.email('Invalid email format');
			}

			schemaFields[fieldUid] = fieldSchema;
		});

		fileUploadFieldUids.current = uids;

		return {
			validationSchema: Yup.object().shape(schemaFields),
			defaultValues: formDefaultValues,
		};
	}, [form]);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm({
		defaultValues,
		shouldFocusError: true,
		mode: 'onChange',
		resolver: yupResolver(validationSchema),
	});

	const onSubmit = async (data) => {
		if (submissionState === 'loading') return false;

		data.sparkform_id = slicePrimary?.sparkform_id;

		fileUploadFieldUids.current.forEach(fieldUid => {
			const inputElement = document.getElementById(fieldUid);
			if (inputElement) {
				const uploadedValue = inputElement.value;
				data[fieldUid] = uploadedValue;
			}
		});

		setSubmissionError(false);
		setTurnstileError('');

		try {
			if (!turnstileTokenRef.current && turnstileWidgetIdRef.current !== null && window.turnstile?.execute) {
				window.turnstile.execute(turnstileWidgetIdRef.current);
				await waitForTurnstileToken(() => turnstileTokenRef.current);
			}
		} catch (error) {
			console.error('Turnstile execute error:', error);
		}

		const turnstileToken = turnstileTokenRef.current;
		if (!turnstileToken) {
			setTurnstileError(turnstileUserMessage());
			resetTurnstileWidget();
			return;
		}

		try {
			setSubmissionState('loading');

			const response = await fetch('/api/submit-form', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
				body: JSON.stringify({
					...data,
					'cf-turnstile-response': turnstileToken,
				}),
			});

			if (response.ok) {
				setSubmissionState('success');
				reset();
				cleanupTurnstileWidget();
			} else {
				const errorData = await response.text();
				setSubmissionState('initial');
				setSubmissionError(true);
				resetTurnstileWidget();
				console.error('API Response Error:', response.status, errorData);
			}
		} catch (error) {
			console.error('Submission Error:', error);
			setSubmissionState('initial');
			setSubmissionError(true);
			resetTurnstileWidget();
		}
	};

	const getFieldError = (fieldUid) => errors[fieldUid]?.message;
	const formData = form?.data;
	const colorStyles = formColorStyles(formData, slicePrimary);
	const topTextStyle = {
		color: slicePrimary?.form_text_color ?? undefined,
	};
	const submitButtonText = isFilled.keyText(formData?.submit_button_text)
		? formData.submit_button_text
		: 'Submit';

	return (
		<BackgroundImage slicePrimary={slicePrimary}>
			<div ref={formSliceRef} className={clsx(styles.wrapper)} style={{ ...sliceStyle(slicePrimary), ...colorStyles }}>

				{submissionState === 'success' ? (
					<div className={clsx(sharedStyles.top)}>
						<div className={clsx(sharedStyles.left)} style={topTextStyle}>
							{isFilled.keyText(slicePrimary?.successful_title) && (
								<h1 className={clsx(sharedStyles.h1)} id={dashify(slicePrimary.successful_title, { separator: '_' })}>
									{slicePrimary.successful_title}
								</h1>
							)}

							{isFilled.richText(slicePrimary.successful_content) && (
								<div className={clsx(styles.copy)}>
									<RichText field={slicePrimary.successful_content} />
								</div>
							)}

						</div>
					</div>
				) : (
					<div className={clsx(sharedStyles.top)}>
						<div className={clsx(sharedStyles.left)} style={topTextStyle}>
							{isFilled.keyText(slicePrimary?.title) && (
								<h1 className={clsx(sharedStyles.h1)} id={dashify(slicePrimary.title, { separator: '_' })}>
									{slicePrimary.title}
								</h1>
							)}

							{isFilled.richText(slicePrimary?.content) && (
								<div className={clsx(styles.copy)}>
									<RichText field={slicePrimary.content} />
								</div>
							)}

						</div>
					</div>
				)}

				{submissionState !== 'success' && (
					<div className={clsx(styles.form_wrapper)}>
						<form
							className={clsx(styles.form,
								submissionState === 'loading' && styles.loading,
								submissionState === 'success' && styles.success,
							)}
							onSubmit={handleSubmit(onSubmit)}
							noValidate>

							<div className={clsx(styles.form_inner)}>
								{form?.data?.slices?.map((fieldSlice) => {
									if (!fieldSlice.primary.title) return null;
									const fieldUid = dashify(fieldSlice.primary.uid, { separator: '_' }) || dashify(fieldSlice.primary.title, { separator: '_' });
									const fieldTitle = fieldSlice.primary.title;
									const fieldRequired = fieldSlice.primary.required;

									return (
										<div key={fieldUid} className={clsx(styles.field)}>
											<div key={fieldUid} className={clsx(styles.field_inner)}>
												<label htmlFor={fieldUid} className={clsx(styles.label)}>
													{fieldTitle} {fieldRequired ? '*' : ''}
												</label>

												<div className={clsx(styles.input_wrap, getFieldError(fieldUid) && styles.has_error)}>
													{fieldSlice.variation === 'default' && (
														<input
															className={clsx(styles.input)}
															type={fieldUid.toLowerCase() === 'email' ? 'email' : (fieldUid.toLowerCase() === 'phone' ? 'tel' : 'text')}
															id={fieldUid}
															{...register(fieldUid)}
														/>
													)}

													{fieldSlice.variation === 'longText' && (
														<textarea
															className={clsx(styles.input, styles.textarea)}
															id={fieldUid}
															rows={8}
															{...register(fieldUid)}
														/>
													)}

													{fieldSlice.variation === 'dropdown' && (
														<select
															className={clsx(styles.input, styles.dropdown)}
															id={fieldUid}
															{...register(fieldUid)}
															defaultValue=""
														>
															<option value="" disabled>Please select</option>
															{fieldSlice.primary.options?.filter(optionItem => optionItem?.option).map((optionItem, optionIndex) => (
																<option key={optionIndex} value={optionItem.option}>
																	{optionItem.option}
																</option>
															))}
														</select>
													)}

													{fieldSlice.variation === 'checkboxes' && (
														<div className={clsx(styles.checkboxes_wrap)}>
															{fieldSlice.primary.options?.filter(optionItem => optionItem?.option).map((optionItem, optionIndex) => (
																<label key={optionIndex} className={clsx(styles.option)}>
																	<input
																		type="checkbox"
																		value={optionItem.option}
																		{...register(fieldUid)}
																		className={clsx(styles.checkbox)}
																	/>
																	<span className={clsx(styles.option_text)}>{optionItem.option}</span>
																</label>
															))}
														</div>
													)}

													{fieldSlice.variation === 'radio' && (
														<div className={clsx(styles.checkboxes_wrap)}>
															{fieldSlice.primary.options?.filter(optionItem => optionItem?.option).map((optionItem, optionIndex) => (
																<label key={optionIndex} className={clsx(styles.option)}>
																	<input
																		type="radio"
																		value={optionItem.option}
																		{...register(fieldUid)}
																		className={clsx(styles.radio)}
																	/>
																	<span className={clsx(styles.option_text)}>{optionItem.option}</span>
																</label>
															))}
														</div>
													)}

													{fieldSlice.variation === 'fileUpload' && (
														<input
															type="hidden"
															id={fieldUid}
															{...register(fieldUid)}
															role="uploadcare-uploader"
															data-public-key={process.env.NEXT_PUBLIC_UPLOADCARE_PUBLIC_KEY}
															data-tabs="file"
															data-system-dialog="true"
														/>
													)}
												</div>
											</div>

											{getFieldError(fieldUid) && (
												<div className={clsx(styles.error)}>
													{getFieldError(fieldUid)}
												</div>
											)}

										</div>
									);
								})}
							</div>

							<div
								ref={turnstileContainerRef}
								className={clsx(styles.turnstile, turnstileInteractive && styles.turnstileInteractive)}
								aria-hidden={!turnstileInteractive}
							/>

							<div className={clsx(styles.submit_row)}>
								<button
									type="submit"
									className={clsx(styles.button)}
									disabled={submissionState === 'loading'}
								>
									{submitButtonText}
								</button>
							</div>

							{turnstileError && (
								<p className={clsx(styles.error)}>
									{turnstileError}
								</p>
							)}

							{submissionError && isFilled.keyText(slicePrimary?.error) && (
								<p className={clsx(styles.error)}>
									{slicePrimary.error}
								</p>
							)}
						</form>
					</div>
				)}

			</div>
		</BackgroundImage>
	);
}
